import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import translations from "./translations.json";

/**
 * Système i18n hybride :
 * - Pré-traductions instantanées (fr + 11 langues) pour l'UI critique
 * - Pour les réponses IA Claude dynamiques : Claude répond directement dans la langue de la question (Touche 10)
 *
 * Usage :
 *   const { t, lang, setLang, langs, isRtl } = useT();
 *   <h1>{t("tagline")}</h1>
 */

export const SUPPORTED_LANGS = [
  { code: "fr", native: "Français", flag: "🇫🇷" },
  { code: "de", native: "Deutsch", flag: "🇩🇪" },
  { code: "ar", native: "العربية", flag: "🇸🇦", rtl: true },
  { code: "en", native: "English", flag: "🇬🇧" },
  { code: "es", native: "Español", flag: "🇪🇸" },
  { code: "ru", native: "Русский", flag: "🇷🇺" },
  { code: "uk", native: "Українська", flag: "🇺🇦" },
  { code: "ro", native: "Română", flag: "🇷🇴" },
  { code: "tr", native: "Türkçe", flag: "🇹🇷" },
  { code: "fa", native: "فارسی", flag: "🇮🇷", rtl: true },
  { code: "ps", native: "پښتو", flag: "🇦🇫", rtl: true },
  { code: "ti", native: "ትግርኛ", flag: "🇪🇷" },
  { code: "ln", native: "Lingála", flag: "🇨🇩" },
];

const RTL_CODES = new Set(SUPPORTED_LANGS.filter((l) => l.rtl).map((l) => l.code));

const LangCtx = createContext({ t: (k) => k, lang: "fr", setLang: () => {}, langs: SUPPORTED_LANGS, isRtl: false });

function detectInitialLang() {
  try {
    const saved = localStorage.getItem("ui_lang");
    if (saved && translations[saved]) return saved;
    const nav = (navigator.language || "fr").toLowerCase().split("-")[0];
    if (translations[nav]) return nav;
  } catch {}
  return "fr";
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = (newLang) => {
    if (!translations[newLang]) return;
    setLangState(newLang);
    try { localStorage.setItem("ui_lang", newLang); } catch {}
  };

  // Apply direction + lang on <html>
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_CODES.has(lang) ? "rtl" : "ltr";
  }, [lang]);

  const t = useMemo(() => {
    const dict = translations[lang] || translations.fr;
    return (key, fallback) => dict[key] || translations.fr[key] || fallback || key;
  }, [lang]);

  const value = useMemo(() => ({
    t, lang, setLang, langs: SUPPORTED_LANGS, isRtl: RTL_CODES.has(lang),
  }), [t, lang]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useT() {
  return useContext(LangCtx);
}
